import React from 'react';
import { Link } from 'react-router-dom';

interface StateLink {
  code: string;
  name: string;
  emoji: string;
  path: string;
}

interface OtherStatesProps {
  currentState: string; // State code to exclude (e.g., 'NY', 'CA', etc.)
}

/**
 * Other States Component - Cross-linking for SEO
 * Displays links to other state license renewal pages
 * Improves internal linking structure and helps distribute page authority
 */
const OtherStates: React.FC<OtherStatesProps> = ({ currentState }) => {
  const allStates: StateLink[] = [
    { code: 'AL', name: 'Alabama',        emoji: '🌻', path: '/al-license-renewal' },
    { code: 'AK', name: 'Alaska',         emoji: '🐻', path: '/ak-license-renewal' },
    { code: 'AZ', name: 'Arizona',        emoji: '🌵', path: '/az-license-renewal' },
    { code: 'AR', name: 'Arkansas',       emoji: '💎', path: '/ar-license-renewal' },
    { code: 'CA', name: 'California',     emoji: '☀️', path: '/ca-license-renewal' },
    { code: 'CO', name: 'Colorado',       emoji: '⛷️', path: '/co-license-renewal' },
    { code: 'CT', name: 'Connecticut',    emoji: '🌲', path: '/ct-license-renewal' },
    { code: 'DC', name: 'Washington D.C.',emoji: '🏛️', path: '/dc-license-renewal' },
    { code: 'DE', name: 'Delaware',       emoji: '🏛️', path: '/de-license-renewal' },
    { code: 'FL', name: 'Florida',        emoji: '🌴', path: '/fl-license-renewal' },
    { code: 'GA', name: 'Georgia',        emoji: '🍑', path: '/ga-license-renewal' },
    { code: 'HI', name: 'Hawaii',         emoji: '🌺', path: '/hi-license-renewal' },
    { code: 'ID', name: 'Idaho',          emoji: '🥔', path: '/id-license-renewal' },
    { code: 'IL', name: 'Illinois',       emoji: '🏢', path: '/il-license-renewal' },
    { code: 'IN', name: 'Indiana',        emoji: '🏎️', path: '/in-license-renewal' },
    { code: 'IA', name: 'Iowa',           emoji: '🌽', path: '/ia-license-renewal' },
    { code: 'KS', name: 'Kansas',         emoji: '🌾', path: '/ks-license-renewal' },
    { code: 'KY', name: 'Kentucky',       emoji: '🐎', path: '/ky-license-renewal' },
    { code: 'LA', name: 'Louisiana',      emoji: '🎷', path: '/la-license-renewal' },
    { code: 'ME', name: 'Maine',          emoji: '🦞', path: '/me-license-renewal' },
    { code: 'MD', name: 'Maryland',       emoji: '🦀', path: '/md-license-renewal' },
    { code: 'MA', name: 'Massachusetts',  emoji: '⚓', path: '/ma-license-renewal' },
    { code: 'MI', name: 'Michigan',       emoji: '🚗', path: '/mi-license-renewal' },
    { code: 'MN', name: 'Minnesota',      emoji: '❄️', path: '/mn-license-renewal' },
    { code: 'MS', name: 'Mississippi',    emoji: '🎶', path: '/ms-license-renewal' },
    { code: 'MO', name: 'Missouri',       emoji: '🌉', path: '/mo-license-renewal' },
    { code: 'MT', name: 'Montana',        emoji: '🦬', path: '/mt-license-renewal' },
    { code: 'NE', name: 'Nebraska',       emoji: '🌽', path: '/ne-license-renewal' },
    { code: 'NV', name: 'Nevada',         emoji: '🎰', path: '/nv-license-renewal' },
    { code: 'NH', name: 'New Hampshire',  emoji: '🍁', path: '/nh-license-renewal' },
    { code: 'NJ', name: 'New Jersey',     emoji: '🏖️', path: '/nj-license-renewal' },
    { code: 'NM', name: 'New Mexico',     emoji: '🌵', path: '/nm-license-renewal' },
    { code: 'NY', name: 'New York',       emoji: '🗽', path: '/ny-license-renewal' },
    { code: 'NC', name: 'North Carolina', emoji: '🌊', path: '/nc-license-renewal' },
    { code: 'ND', name: 'North Dakota',   emoji: '🌾', path: '/nd-license-renewal' },
    { code: 'OH', name: 'Ohio',           emoji: '🏛️', path: '/oh-license-renewal' },
    { code: 'OK', name: 'Oklahoma',       emoji: '🌾', path: '/ok-license-renewal' },
    { code: 'OR', name: 'Oregon',         emoji: '🌲', path: '/or-license-renewal' },
    { code: 'PA', name: 'Pennsylvania',   emoji: '🔔', path: '/pa-license-renewal' },
    { code: 'RI', name: 'Rhode Island',   emoji: '⚓', path: '/ri-license-renewal' },
    { code: 'SC', name: 'South Carolina', emoji: '🌴', path: '/sc-license-renewal' },
    { code: 'SD', name: 'South Dakota',   emoji: '🦅', path: '/sd-license-renewal' },
    { code: 'TN', name: 'Tennessee',      emoji: '🎵', path: '/tn-license-renewal' },
    { code: 'TX', name: 'Texas',          emoji: '🤠', path: '/tx-license-renewal' },
    { code: 'UT', name: 'Utah',           emoji: '⛷️', path: '/ut-license-renewal' },
    { code: 'VT', name: 'Vermont',        emoji: '🍁', path: '/vt-license-renewal' },
    { code: 'VA', name: 'Virginia',       emoji: '🏛️', path: '/va-license-renewal' },
    { code: 'WA', name: 'Washington',     emoji: '🌲', path: '/wa-license-renewal' },
    { code: 'WV', name: 'West Virginia',  emoji: '🌳', path: '/wv-license-renewal' },
    { code: 'WI', name: 'Wisconsin',      emoji: '🧀', path: '/wi-license-renewal' },
    { code: 'WY', name: 'Wyoming',        emoji: '🦬', path: '/wy-license-renewal' },
  ];

  // Filter out the current state
  const otherStates = allStates.filter(state => state.code !== currentState);

  return (
    <section 
      aria-labelledby="other-states-heading"
      style={{ 
        padding: '80px 0', 
        backgroundColor: '#f8f9fa' 
      }}
    >
      <div className="container">
        <div className="text-center mb-5">
          <h2 
            id="other-states-heading"
            style={{ 
              fontSize: '2.5rem', 
              fontWeight: '700', 
              marginBottom: '1rem' 
            }}
          >
            License Renewal in Other States
          </h2>
          <p style={{ fontSize: '1.2rem', color: '#6c757d' }}>
            We support professional license tracking across multiple states
          </p>
        </div>

        <div className="row g-3">
          {otherStates.map((state) => (
            <div key={state.code} className="col-6 col-md-4 col-lg-2">
              <Link
                to={state.path}
                className="text-decoration-none"
                style={{ display: 'block' }}
              >
                <div 
                  className="card h-100 text-center border-0 shadow-sm"
                  style={{ 
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  }}
                >
                  <div className="card-body p-3">
                    <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>
                      {state.emoji}
                    </div>
                    <div style={{ 
                      fontSize: '0.9rem', 
                      fontWeight: '600',
                      color: '#212529'
                    }}>
                      {state.name}
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>

        <div className="text-center mt-4">
          <p style={{ fontSize: '0.95rem', color: '#6c757d' }}>
            All 50 states + D.C. supported.{' '}
            <Link to="/register" style={{ marginLeft: '0.25rem' }}>
              Start tracking free →
            </Link>
          </p>
        </div>
      </div>
    </section>
  );
};

export default OtherStates;
